import { View } from 'react-native';
import {
  Button,
  Text,
  TextInput,
  useTheme,
  HelperText,
  RadioButton,
} from 'react-native-paper';
import { useForm, Controller } from 'react-hook-form';
import { Business, Props } from '../types';
import { db } from '../Firebase/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import {
  setBusinesses,
  setSelectedBusiness,
  setEditingBusiness,
  setWebModalShown,
} from '../redux/businessListSlice';
import { setSnackbar } from '../redux/snackbarSlice';

export default function EditBusinessForm({ navigation }: Partial<Props>) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { businesses, selectedBusiness, editingBusiness } = useAppSelector(
    (state) => state.businessList
  );
  const { organization } = useAppSelector((state) => state.organization);
  interface IFormInput {
    name: string;
    address: string;
    status: string;
  }

  if (!selectedBusiness) {
    return <></>;
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<IFormInput>({
    defaultValues: {
      name: selectedBusiness.name,
      address: selectedBusiness.address,
      status: selectedBusiness.status,
    },
  });
  const updateBusiness = async (data: IFormInput) => {
    dispatch(setEditingBusiness(true));
    const docRef = doc(
      db,
      `organizations/${organization.id}/businesses`,
      selectedBusiness.id
    );
    const formData = {
      address: data.address,
      name: data.name,
      status: data.status,
    };
    await updateDoc(docRef, formData);
    const updatedData = {
      address: data.address,
      id: selectedBusiness.id,
      name: data.name,
      status: data.status,
      latitude: selectedBusiness.latitude,
      longitude: selectedBusiness.longitude,
      placeId: selectedBusiness.placeId,
    } as Business;

    const index = businesses.indexOf(selectedBusiness);

    dispatch(
      setBusinesses([
        ...businesses.slice(0, index),
        updatedData,
        ...businesses.slice(index + 1),
      ])
    );

    dispatch(setSelectedBusiness(updatedData));
    dispatch(setEditingBusiness(false));
    dispatch(
      setSnackbar({
        severity: 'success',
        message: 'Edited business successfully!',
      })
    );
    reset();
    if (navigation) {
      navigation.goBack();
    } else {
      dispatch(setWebModalShown('None'));
    }
  };
  const onSubmit = (data: IFormInput) => updateBusiness(data);

  return (
    <View style={{ padding: 20 }}>
      <Controller
        control={control}
        rules={{
          required: true,
        }}
        render={({ field: { onChange, value } }) => (
          <TextInput
            mode="flat"
            label="Business Name *"
            onChangeText={onChange}
            value={value}
            style={{ backgroundColor: 'transparent', marginBottom: 12 }}
            error={errors.name ? true : false}
          />
        )}
        name="name"
      />
      <HelperText
        type="error"
        visible={errors.name ? true : false}
        style={{ marginBottom: 12 }}
      >
        This is a required field.
      </HelperText>

      <Controller
        control={control}
        rules={{
          required: true,
        }}
        render={({ field: { onChange, value } }) => (
          <TextInput
            label="Address *"
            onChangeText={onChange}
            value={value}
            style={{ backgroundColor: 'transparent' }}
            error={errors.address ? true : false}
          />
        )}
        name="address"
      />
      {errors.address && <Text>This is required.</Text>}

      <Controller
        control={control}
        rules={{
          required: true,
        }}
        render={({ field: { onChange, value } }) => (
          <RadioButton.Group onValueChange={onChange} value={value}>
            <Text
              variant="labelLarge"
              style={{ marginTop: 40, paddingLeft: 10 }}
            >
              Status
            </Text>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              <RadioButton.Item mode="android" label="New" value="New" />
              <RadioButton.Item mode="android" label="Active" value="Active" />
              <RadioButton.Item mode="android" label="Closed" value="Closed" />
            </View>
          </RadioButton.Group>
        )}
        name="status"
      />
      <HelperText
        type="error"
        visible={errors.status ? true : false}
        style={{ marginBottom: 12 }}
      >
        This is a required field.
      </HelperText>

      <Button
        mode="contained"
        loading={editingBusiness}
        disabled={editingBusiness}
        color={theme.colors.primary}
        style={{ borderRadius: 4, marginTop: 240 }}
        onPress={handleSubmit(onSubmit)}
      >
        {editingBusiness ? '' : 'Submit'}
      </Button>
    </View>
  );
}
